import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Copy, Loader2 } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useCsAccountDetailPage } from '@/hooks/useCsAccountDetailPage'
import { CsAccountHealthMatrix } from '@/components/Cs/CsAccountHealthMatrix'
import { CsAccountDados } from '@/components/Cs/CsAccountDados'
import { CsAccountAgenda } from '@/components/Cs/CsAccountAgenda'
import { CsAccountRelacionamento } from '@/components/Cs/CsAccountRelacionamento'
import { CsAccountRetencao } from '@/components/Cs/CsAccountRetencao'
import { CsAccountExpansao } from '@/components/Cs/CsAccountExpansao'
import { CsAccountTratativas } from '@/components/Cs/CsAccountTratativas'
import { CsAccountNps } from '@/components/Cs/CsAccountNps'
import { CsAccountHistorico } from '@/components/Cs/CsAccountHistorico'
import { CsSection } from '@/components/Cs/CsSection'
import { Button } from '@/components/ui/Button'
import { CsHealthBadge } from '@/components/Cs/CsHealthBadge'

export default function CsAccountDetailPage() {
  const { id } = useParams<{ id: string }>()
  const page = useCsAccountDetailPage(id)

  if (page.loading || !page.account) {
    return (
      <DashboardLayout title='Conta'>
        <div className='flex h-48 items-center justify-center'>
          <Loader2 className='animate-spin text-[var(--brand-primary)]' size={28} />
        </div>
      </DashboardLayout>
    )
  }

  const acc = page.account
  const empresaNome = acc.empresa.nomeFantasia || acc.empresa.razaoSocial || acc.empresa.nome || 'Cliente'

  return (
    <DashboardLayout title={empresaNome}>
      <div className='space-y-5'>
        <header className='flex flex-col gap-3 border-b border-[var(--glass-border)] pb-4 sm:flex-row sm:items-start sm:justify-between'>
          <div className='min-w-0 space-y-2'>
            <Link
              to='/cs/carteira'
              className='inline-flex items-center gap-1.5 text-xs font-medium text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]'
            >
              <ArrowLeft size={13} />
              Voltar à carteira
            </Link>

            <div className='flex flex-wrap items-center gap-2'>
              <CsHealthBadge score={acc.healthScoreFinal} band={acc.healthBand} compact />
              {acc.classificacao && (
                <span className='rounded-md border border-[var(--glass-border)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-secondary)]'>
                  {acc.classificacao}
                </span>
              )}
            </div>

            <p className='text-sm text-[var(--text-muted)]'>
              <span className='text-[var(--text-secondary)]'>{acc.deal.nome}</span>
              {acc.csm?.name ? (
                <>
                  <span className='mx-1.5 text-[var(--glass-border)]'>·</span>
                  CSM {acc.csm.name}
                </>
              ) : null}
            </p>
          </div>

          <Button
            variant='secondary'
            size='sm'
            icon={<Copy size={14} />}
            onClick={() => void page.copyPortalLink()}
            className='shrink-0 self-start'
          >
            Copiar link do portal
          </Button>
        </header>

        <CsAccountHealthMatrix account={acc} onSave={page.saveFields} />

        <div className='grid gap-4 lg:grid-cols-2'>
          <CsAccountDados account={acc} onSave={page.saveFields} />
          <CsAccountAgenda account={acc} onSave={page.saveFields} />
          <CsAccountRelacionamento account={acc} onSave={page.saveFields} />
          <CsAccountRetencao account={acc} onSave={page.saveFields} />
        </div>

        <CsAccountHistorico accountId={acc.id} dealId={acc.deal.id} />

        <CsAccountExpansao
          account={acc}
          onCreate={page.addExpansao}
          onUpdateStatus={page.setExpansaoStatus}
        />

        <div className='grid gap-4 lg:grid-cols-2'>
          <CsAccountTratativas
            account={acc}
            onCreate={page.addTratativa}
            onAddInteraction={page.addInteraction}
          />
          <CsAccountNps account={acc} onCreate={page.addNps} />
        </div>

        {acc.onboarding && (
          <CsSection title='Onboarding'>
            <ul className='space-y-1 text-sm'>
              {acc.onboarding.tasks.map((t) => (
                <li key={t.id} className='flex items-center gap-2'>
                  <span className={t.status === 'CONCLUIDO' ? 'text-emerald-400' : 'text-[var(--text-muted)]'}>
                    {t.status === 'CONCLUIDO' ? '✓' : '○'}
                  </span>
                  {t.titulo}
                </li>
              ))}
            </ul>
          </CsSection>
        )}
      </div>
    </DashboardLayout>
  )
}
